/* ---------------------------------------------------------------------------
   PDP Kimonix recommendations.

   Backed by a Kimonix AI element (POST /get_ai_element_products). Their own
   kimonix_elements.js auto-render is deliberately unused: it injects .kmnx-*
   markup that will not match Figma 263:122, and it only handles recommendation
   elements, not AI elements. Either way the API returns just { id, handle } and
   expects the frontend to fetch the rest.

   So: ask Kimonix which products, then render each one through the theme's own
   card via /products/<handle>?view=kimonix-card. That keeps the discount chain,
   market-correct money and the size picker identical to the rest of the site
   instead of reimplementing prices in JS.

   Their attribution endpoints are still called, so reporting keeps working.
--------------------------------------------------------------------------- */
(() => {
  const API = 'https://api.kimonix.com';

  const localization = () => {
    if (!window.Shopify) return undefined;
    const l = {};
    ['country', 'currency', 'locale', 'routes'].forEach((k) => {
      if (window.Shopify[k]) l[k] = window.Shopify[k];
    });
    return Object.keys(l).length ? l : undefined;
  };

  const post = (path, body) =>
    fetch(API + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

  const getCart = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

  const cartProductIds = () =>
    getCart()
      .then((c) => (c && c.items ? [...new Set(c.items.map((i) => i.product_id).filter(Boolean))].slice(0, 20) : []))
      .catch(() => []);

  async function fetchProducts(section) {
    const element_id = section.dataset.elementId;
    const productId = Number(section.dataset.productId) || undefined;
    const limit = parseInt(section.dataset.maxProducts, 10) || 8;

    // AI elements key off product ids, not handles. product_id drives the
    // frequently-bought-together / similar / complete-the-look strategies;
    // cart and recently-viewed are personalisation signals.
    const res = await post('/get_ai_element_products', {
      element_id,
      product_id: productId,
      cart_product_ids: await cartProductIds(),
      limit,
      localization: localization()
    });

    if (!res.ok) {
      // A 400 here is almost always "Element not found" -- a wrong id. Note the
      // error response carries no CORS headers, so a bad id surfaces in the
      // browser as "Failed to fetch" rather than a readable status.
      console.warn('[kimonix] element', element_id, '->', res.status);
      return [];
    }

    const data = await res.json();
    const products = (data.products || []).filter((p) => p && p.handle);

    // The card markup carries its own product id, so only handles are needed
    // from here -- they address /products/<handle>?view=kimonix-card.
    return products.map((p) => p.handle);
  }

  // One request per card. They are small, parallel, and only fire once the
  // section is near the viewport, so this stays cheap on a page this heavy.
  const fetchCard = (handle) =>
    fetch(`/products/${encodeURIComponent(handle)}?view=kimonix-card`)
      .then((r) => (r.ok ? r.text() : null))
      .catch(() => null);

  function buildDots(section, track) {
    const dots = section.querySelector('[data-kmnx-dots]');
    if (!dots) return () => {};

    const pages = () => Math.max(1, Math.ceil(track.scrollWidth / Math.max(1, track.clientWidth)));

    const render = () => {
      const total = pages();
      if (total < 2) {
        dots.innerHTML = '';
        return;
      }
      if (dots.children.length !== total) {
        dots.innerHTML = '';
        for (let i = 0; i < total; i++) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'pdp-kmnx__dot';
          b.setAttribute('aria-label', `Go to page ${i + 1}`);
          b.addEventListener('click', () => {
            track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
          });
          dots.appendChild(b);
        }
      }
      const active = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      [...dots.children].forEach((d, i) => d.classList.toggle('is-active', i === active));
    };

    render();
    return render;
  }

  function wireArrows(section, track, onMove) {
    const prev = section.querySelector('.pdp-kmnx__arrow--prev');
    const next = section.querySelector('.pdp-kmnx__arrow--next');
    const step = () => track.clientWidth;

    prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));

    const sync = () => {
      const max = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= max - 1;
      onMove();
    };

    // scroll fires a lot on a flick, so coalesce into one frame
    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          sync();
        });
      },
      { passive: true }
    );

    sync();
  }

  // ---- card behaviour -------------------------------------------------
  // This card is ours (snippets/pdp-kimonix-card.liquid), not the shared PLP
  // card, so none of product-card.js applies and the three interactions are
  // wired here: swatch, size, add.

  const variantsOf = (card) => {
    try {
      return JSON.parse(card.querySelector('[data-kmnx-variants]').textContent);
    } catch (_) {
      return [];
    }
  };

  function wireCard(card) {
    const addBtn = card.querySelector('[data-kmnx-add]');
    const sizeSel = card.querySelector('[data-kmnx-size]');
    const priceEl = card.querySelector('[data-kmnx-price]');
    const prices = card.querySelector('[data-kmnx-prices]');
    const img = card.querySelector('.kmnx-card__img--primary') || card.querySelector('.kmnx-card__img');
    const hoverImg = card.querySelector('[data-kmnx-hover-img]');
    const hoverMap = (() => {
      const el = card.querySelector('[data-kmnx-hover-map]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    })();
    const variants = variantsOf(card);

    // --- material swatch: repice, reimage, retarget the add button ---
    card.querySelectorAll('.kmnx-card__swatch input').forEach((input) => {
      input.addEventListener('change', () => {
        const material = input.value;

        card.querySelectorAll('.kmnx-card__swatch').forEach((l) => {
          l.classList.toggle('is-selected', l.contains(input));
        });

        // Prices come from the Liquid-rendered <template>, one entry per
        // material, so money formatting and the discount chain stay correct
        // for the active market -- Shopify.formatMoney is not available here.
        if (prices && priceEl) {
          const entry = [...prices.content.children].find((el) => el.dataset.material === material);
          if (entry) priceEl.innerHTML = entry.innerHTML;
        }

        const match = variants.find((v) => v.option1 === material && (!sizeSel || !sizeSel.value || String(v.id) === sizeSel.value))
          || variants.find((v) => v.option1 === material);

        if (match) {
          if (addBtn) addBtn.dataset.variantId = match.id;
          if (img && match.featured_image && match.featured_image.src) {
            img.removeAttribute('srcset');
            img.src = match.featured_image.src;
          }
        }

        /* The hover shot belongs to the colourway, so it has to move with the
           swatch. Hidden rather than left stale when a material has no second
           image of its own -- hovering then just scales the primary. */
        if (hoverImg && hoverMap) {
          const next = hoverMap[material];
          if (next && next !== img.currentSrc && next !== img.getAttribute('src')) {
            hoverImg.removeAttribute('srcset');
            hoverImg.src = next;
            hoverImg.hidden = false;
            if (hoverImg.parentElement) hoverImg.parentElement.classList.add('kmnx-card__media--dual');
          } else {
            hoverImg.hidden = true;
            if (hoverImg.parentElement) hoverImg.parentElement.classList.remove('kmnx-card__media--dual');
          }
        }
      });
    });

    // --- size dropdown: it carries the variant id directly ---
    if (sizeSel) {
      sizeSel.addEventListener('change', () => {
        if (addBtn && sizeSel.value) addBtn.dataset.variantId = sizeSel.value;
      });
    }

    // --- add to cart ---
    if (!addBtn) return;
    addBtn.addEventListener('click', async () => {
      // A sized product needs a size first; the design has no separate error
      // state, so just send them to the dropdown.
      if (sizeSel && !sizeSel.value) {
        sizeSel.focus();
        return;
      }

      const id = Number(addBtn.dataset.variantId);
      if (!id || addBtn.classList.contains('is-loading')) return;

      const label = addBtn.querySelector('[data-kmnx-add-label]');
      const was = label ? label.textContent : '';
      addBtn.classList.add('is-loading');

      const before = snapshotCart(await getCart());

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: 1 }] })
        });
        if (!res.ok) throw new Error(res.status);
        if (label) label.textContent = 'Added';
        document.dispatchEvent(new CustomEvent('kmnx:added', { detail: { variantId: id } }));
        reportAdd(card, before);
      } catch (e) {
        if (label) label.textContent = 'Try again';
      } finally {
        addBtn.classList.remove('is-loading');
        // never leave the button stuck on a transient label
        setTimeout(() => {
          if (label) label.textContent = was;
        }, 2000);
      }
    });
  }

  // ---- Kimonix attribution -------------------------------------------
  // We render the cards ourselves, so their script reports nothing; these are
  // the documented endpoints. Fire-and-forget throughout: reporting must never
  // affect the shopper, so every path swallows its errors.

  const snapshotCart = (cart) =>
    Object.fromEntries((cart && cart.items ? cart.items : []).map((i) => [i.key, i]));

  const ctxOf = (card) => {
    const section = card.closest('[data-pdp-kimonix]');
    if (!section) return null;
    return {
      shop_domain: section.dataset.shopDomain,
      element_id: section.dataset.elementId,
      product_id: Number(card.dataset.productId) || null
    };
  };

  // Which variant the card is currently offering.
  const variantOf = (card) => {
    const sizeSel = card.querySelector('[data-kmnx-size]');
    if (sizeSel && sizeSel.value) return Number(sizeSel.value);
    const addBtn = card.querySelector('[data-kmnx-add]');
    return Number(addBtn && addBtn.dataset.variantId) || null;
  };

  async function reportAdd(card, before) {
    const ctx = ctxOf(card);
    if (!ctx || !ctx.product_id) return;

    const cart = await getCart();
    if (!cart) return;
    const after = snapshotCart(cart);

    // the line that appeared, or the one whose quantity went up
    let line = null;
    let added = 0;
    for (const [key, item] of Object.entries(after)) {
      const was = before[key] ? before[key].quantity : 0;
      if (item.quantity > was) {
        line = item;
        added = item.quantity - was;
        break;
      }
    }
    if (!line) return;

    // final_line_price is cents of the presentment currency; Kimonix wants the
    // shop's base currency, so undo the market rate.
    const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    const money = Number(line.final_line_price || 0) / 100 / rate;

    post('/elements_report_add_to_cart', {
      shop_domain: ctx.shop_domain,
      cart_token: cart.token,
      element_id: ctx.element_id,
      product_id: ctx.product_id,
      variant_id: Number(line.variant_id),
      num_items_added_to_cart: added,
      money_added_to_cart: Number(money.toFixed(2))
    }).catch(() => {});
  }

  // Clicking through to a product page.
  function wireReporting(section, track) {
    track.addEventListener('click', (e) => {
      const link = e.target.closest('a[href*="/products/"]');
      if (!link) return;
      const card = link.closest('[data-kmnx-card]');
      if (!card) return;

      const ctx = ctxOf(card);
      const variant_id = variantOf(card);
      if (!ctx || !ctx.product_id || !variant_id) return;

      getCart()
        .then((cart) =>
          post('/elements/analytics/product_page_referrals', {
            shop_domain: ctx.shop_domain,
            element_id: ctx.element_id,
            product_id: ctx.product_id,
            variant_id,
            cart_token: (cart && cart.token) || null
          })
        )
        .catch(() => {});
    });
  }

  async function init(section) {
    const track = section.querySelector('[data-kmnx-track]');
    if (!track) return;

    const handles = await fetchProducts(section);
    // Nothing to show: take the section out entirely rather than leave a
    // collapsed shell behind.
    if (!handles.length) return section.remove();

    const cards = await Promise.all(handles.map(fetchCard));
    const html = cards
      .filter(Boolean)
      .map((c) => `<li class="pdp-kmnx__item">${c}</li>`)
      .join('');
    if (!html) return section.remove();

    track.innerHTML = html;
    section.classList.remove('is-idle');

    track.querySelectorAll('[data-kmnx-card]').forEach(wireCard);

    const redraw = buildDots(section, track);
    wireArrows(section, track, redraw);
    wireReporting(section, track);

    // Card images land after the markup does, which changes scrollWidth.
    if ('ResizeObserver' in window) {
      let last = 0;
      new ResizeObserver(() => {
        if (track.scrollWidth === last) return; // only on a real change
        last = track.scrollWidth;
        redraw();
      }).observe(track);
    }
  }

  function boot() {
    document.querySelectorAll('[data-pdp-kimonix]').forEach((section) => {
      if (section.dataset.kmnxLoaded) return;

      // Below the fold, so nothing is requested until it is nearly in view.
      if (!('IntersectionObserver' in window)) {
        section.dataset.kmnxLoaded = '1';
        init(section);
        return;
      }

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            io.disconnect();
            section.dataset.kmnxLoaded = '1';
            init(section);
          });
        },
        { rootMargin: '600px 0px' }
      );
      io.observe(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
